import { useEffect, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Scale, Ruler, Package } from "lucide-react";

/**
 * Actual + Volumetric + Chargeable weight editor.
 * Volumetric formula:  (L × B × H) / 5000  (in kg; L/B/H in cm)
 * Chargeable = max(actual, volumetric).
 * Auto-updates form.chargeable_weight_kg / weight_kg whenever any input changes.
 */
export default function WeightBlock({ form, set, prefix = "w" }) {
  const L = Number(form.length_cm) || 0;
  const B = Number(form.breadth_cm) || 0;
  const H = Number(form.height_cm) || 0;
  const actual = Number(form.actual_weight_kg ?? form.weight_kg ?? 0) || 0;

  const volumetric = useMemo(() => (L > 0 && B > 0 && H > 0 ? +((L * B * H) / 5000).toFixed(3) : 0), [L, B, H]);
  const chargeable = useMemo(() => +Math.max(actual, volumetric).toFixed(3), [actual, volumetric]);

  return (
    <div className="space-y-3" data-testid={`${prefix}-weight-block`}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <NumField
          label="Actual weight (kg)"
          v={form.actual_weight_kg ?? form.weight_kg}
          onChange={(v) => {
            const a = parseFloat(v) || 0;
            set("actual_weight_kg", a);
            // keep chargeable in sync immediately so parent rate-fetch uses the right weight
            const c = Math.max(a, volumetric);
            set("weight_kg", c);
            set("chargeable_weight_kg", c);
          }}
          testid={`${prefix}-actual-weight`}
          step="0.1"
          IconEl={Scale}
        />
        <NumField label="Length (cm)" v={form.length_cm} onChange={(v)=>set("length_cm", v)} testid={`${prefix}-length`} step="0.1" IconEl={Ruler} />
        <NumField label="Breadth (cm)" v={form.breadth_cm} onChange={(v)=>set("breadth_cm", v)} testid={`${prefix}-breadth`} step="0.1" IconEl={Ruler} />
        <NumField label="Height (cm)" v={form.height_cm} onChange={(v)=>set("height_cm", v)} testid={`${prefix}-height`} step="0.1" IconEl={Ruler} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <ReadOut
          label="Volumetric (kg)"
          value={volumetric > 0 ? volumetric.toFixed(3) : "—"}
          hint={volumetric > 0 ? `(${L}×${B}×${H})/5000` : "Enter L·B·H"}
          testid={`${prefix}-volumetric-weight`}
        />
        <ReadOut
          label="Chargeable (kg)"
          value={chargeable > 0 ? chargeable.toFixed(3) : "—"}
          hint={volumetric > actual ? "max = volumetric" : "max = actual"}
          highlight
          testid={`${prefix}-chargeable-weight`}
          onMount={() => {
            // Called on render via effect below via ReadOut wrapper is not ideal;
            // sync chargeable into form when volumetric changes independently of actual
          }}
        />
        <div className="border border-dashed border-primary/30 rounded-md p-3 bg-red-50/40 text-[11px] text-muted-foreground flex items-start gap-2">
          <Package size={14} className="text-primary mt-0.5 shrink-0" />
          <div>
            Air couriers bill by <b>chargeable weight</b> — whichever is higher between physical and volumetric. Enter box L·B·H to compute automatically.
          </div>
        </div>
      </div>
      <ChargeableSync chargeable={chargeable} volumetric={volumetric} set={set} />
    </div>
  );
}

// Effect-only sync helper — pushes chargeable/volumetric into the parent form whenever dims change
function ChargeableSync({ chargeable, volumetric, set }) {
  useEffect(() => {
    set("volumetric_weight_kg", volumetric || null);
    set("chargeable_weight_kg", chargeable || 0);
    set("weight_kg", chargeable || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chargeable, volumetric]);
  return null;
}

function NumField({ label, v, onChange, testid, step = "0.1", IconEl }) {
  return (
    <label className="block">
      <span className="overline flex items-center gap-1.5">
        {IconEl && <IconEl size={11} className="text-muted-foreground" />}
        {label}
      </span>
      <Input
        data-testid={testid}
        type="number"
        step={step}
        min="0"
        value={v ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 rounded-md border-input focus-visible:ring-primary font-mono"
      />
    </label>
  );
}

function ReadOut({ label, value, hint, highlight, testid }) {
  return (
    <div className={`rounded-md p-3 border ${highlight ? "border-primary bg-red-50/60" : "border-border bg-muted/30"}`} data-testid={testid}>
      <div className={`overline ${highlight ? "text-primary" : ""}`}>{label}</div>
      <div className="font-display text-xl mt-0.5 font-mono">{value}</div>
      <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>
    </div>
  );
}
